import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { emptyApi } from '@/app/store/api/emptyApi';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppDispatch, setAuthInitialized, resetAuthInitialized } from '@/app/store';
import { useApplyAuthPayload } from './useApplyAuthPayload';
import { useApplyClientProfile } from './useApplyClientProfile';
import {
  AUTH_BROADCAST_CHANNEL,
  AUTH_BROADCAST_SOURCE_ID,
  AuthBroadcastMessageType,
  type AuthBroadcastMessage,
  useAuthTelegramLoginMutation,
  useLazyAuthClientProfileQuery,
} from '@/entities';
import type { AuthPayload } from './useApplyAuthPayload';
import {
  isTokenStale,
  getRefreshToken,
  isTelegramWebApp,
  useTokenRefresh,
  cancelRefreshTimer,
  getLocalizedReduxErrorMessage,
} from '@/shared/lib';

export const useAuthInit = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('errors');

  const { refresh } = useTokenRefresh();
  const [telegramLogin] = useAuthTelegramLoginMutation();
  const [fetchProfile] = useLazyAuthClientProfileQuery();
  const applyAuthPayload = useApplyAuthPayload();
  const applyClientProfile = useApplyClientProfile();

  const isRunningRef = useRef(false);
  const authGenerationRef = useRef(0);
  const latestCrossTabAuthRef = useRef<AuthPayload | null>(null);
  const applyLatestCrossTabAuthRef = useRef<() => void>(() => undefined);
  const [userLanguage, setUserLanguage] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);

  applyLatestCrossTabAuthRef.current = () => {
    const payload = latestCrossTabAuthRef.current;
    if (!payload) return;

    const applied = applyAuthPayload(payload);
    setUserLanguage(applied.userLanguage);
    dispatch(setAuthInitialized());
    setIsLoading(false);
  };

  const initAuth = useCallback(async () => {
    if (isRunningRef.current) return;
    isRunningRef.current = true;
    const generation = authGenerationRef.current;
    try {
      if (getRefreshToken()) {
        try {
          const tokens = await refresh();
          if (generation !== authGenerationRef.current) {
            applyLatestCrossTabAuthRef.current();
            return;
          }
          const profileResult = await fetchProfile().unwrap();
          if (generation !== authGenerationRef.current) {
            applyLatestCrossTabAuthRef.current();
            return;
          }
          const applied = applyAuthPayload({ profile: profileResult.data, auth: tokens });
          setUserLanguage(applied.userLanguage);
          dispatch(setAuthInitialized());
          return;
        } catch {
          if (generation !== authGenerationRef.current) {
            applyLatestCrossTabAuthRef.current();
            return;
          }
          // refresh or /profile failed → fall through
        }
      }

      if (isTelegramWebApp()) {
        const loginResult = await telegramLogin({ clientTelegramLoginDto: {} }).unwrap();
        if (generation !== authGenerationRef.current) {
          applyLatestCrossTabAuthRef.current();
          return;
        }
        if (!loginResult.data.profile) {
          console.error('Telegram login response had null profile');
          return;
        }
        const applied = applyAuthPayload({
          profile: loginResult.data.profile,
          auth: loginResult.data.auth,
        });
        setUserLanguage(applied.userLanguage);
        dispatch(setAuthInitialized());
        return;
      }
    } catch (error) {
      if (generation !== authGenerationRef.current) {
        applyLatestCrossTabAuthRef.current();
        return;
      }
      toast.error(getLocalizedReduxErrorMessage(error ?? '', t));
    } finally {
      isRunningRef.current = false;
      setIsLoading(false);
    }
  }, [refresh, fetchProfile, applyAuthPayload, telegramLogin, dispatch, t]);

  useEffect(() => {
    initAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAuthBroadcastRef = useRef<(event: MessageEvent<AuthBroadcastMessage>) => void>(() => undefined);
  handleAuthBroadcastRef.current = (event) => {
    if (event.data?.type === AuthBroadcastMessageType.EmailVerificationSuccess) {
      if (event.data.sourceId === AUTH_BROADCAST_SOURCE_ID) return;
      dispatch(emptyApi.util.resetApiState());
      applyClientProfile(event.data.payload);
      return;
    }

    if (
      event.data?.type !== AuthBroadcastMessageType.MagicLinkSuccess ||
      event.data.sourceId === AUTH_BROADCAST_SOURCE_ID
    )
      return;

    authGenerationRef.current += 1;
    latestCrossTabAuthRef.current = event.data.payload;
    dispatch(emptyApi.util.resetApiState());
    applyLatestCrossTabAuthRef.current();
  };

  useEffect(() => {
    const channel = new BroadcastChannel(AUTH_BROADCAST_CHANNEL);
    channel.onmessage = (event: MessageEvent<AuthBroadcastMessage>) => handleAuthBroadcastRef.current(event);
    return () => channel.close();
  }, []);

  const handleResumeRef = useRef<() => void>(() => undefined);
  handleResumeRef.current = () => {
    if (document.visibilityState !== 'visible') return;
    if (isRunningRef.current) return;
    if (!getRefreshToken() || !isTokenStale()) return;
    cancelRefreshTimer();
    dispatch(emptyApi.util.resetApiState());
    dispatch(resetAuthInitialized());
    setIsLoading(true);
    void initAuth();
  };

  useEffect(() => {
    const onResume = () => handleResumeRef.current();
    document.addEventListener('visibilitychange', onResume);
    window.addEventListener('pageshow', onResume);
    return () => {
      document.removeEventListener('visibilitychange', onResume);
      window.removeEventListener('pageshow', onResume);
    };
  }, []);

  return { getUserLanguage: () => userLanguage, isLoading };
};
